import { useCallback, useMemo } from 'react';
import useMuiTableBuilderV2 from 'hooks/useMuiTableBuilderV2';
import { getTableConfig } from './tableConfig';
import { useDataDeletionListItemBuilder } from './useDataDeletionListItemBuilder';
import { TeamPendingDeletion } from 'TeamModule/services/GetTeamsPendingDeletionApi';
import { Box, Button, TablePaginationProps } from '@mui/material';
import { ApprovalPersonnel } from 'TeamModule/constants/approval';
import { useDataDeletionBulkActionMenu } from 'TeamModule/components/DataDeletionBulkActionMenu/useDataDeletionBulkActionMenu';
import TeamsPaginationFooter from 'TeamModule/components/TeamsV2Page/components/TeamsPaginationFooter';

const tableConfig = getTableConfig();

export const DataDeletionReviewTable = ({
  teams,
  isLoading,
  selectedIds,
  hasSelectionOnPage,
  allOnPageSelected,
  isIndeterminateOnPage,
  onToggleRow,
  onToggleSelectAll,
  paginationProps,
  onBulkApprove,
  onBulkUnapprove,
  orderBy,
  orderDirection,
  onSort,
}: {
  teams: TeamPendingDeletion[];
  isLoading: boolean;
  selectedIds: Set<string>;
  hasSelectionOnPage: boolean;
  allOnPageSelected: boolean;
  isIndeterminateOnPage: boolean;
  onToggleRow: (teamId: string) => void;
  onToggleSelectAll: (checked: boolean) => void;
  paginationProps: TablePaginationProps & { indexBase?: 0 | 1 };
  onBulkApprove: (type: ApprovalPersonnel) => void;
  onBulkUnapprove: (type: ApprovalPersonnel) => void;
  orderBy: string;
  orderDirection: (typeof import('@constants').ORDER_DIRECTION)[keyof typeof import('@constants').ORDER_DIRECTION];
  onSort: (columnId: string) => void;
}) => {
  const listItemsBuilder = useDataDeletionListItemBuilder();
  const mainList = useMemo(() => {
    return { listItems: listItemsBuilder({ teams }) };
  }, [listItemsBuilder, teams]);

  const _customRowProps = useMemo(
    () => ({
      selectedIds,
      onToggleRow,
      allOnPageSelected,
      isIndeterminateOnPage,
      onToggleSelectAll,
      orderBy,
      orderDirection,
      onSort,
    }),
    [
      allOnPageSelected,
      isIndeterminateOnPage,
      onSort,
      onToggleRow,
      onToggleSelectAll,
      orderBy,
      orderDirection,
      selectedIds,
    ],
  );

  const { openApproveMenu, openUnapproveMenu } = useDataDeletionBulkActionMenu();

  const renderHeaderTools = useCallback(() => {
    const disabled = !hasSelectionOnPage;
    return (
      <Box display="flex" alignItems="center" ml="auto">
        <Button
          variant="contained"
          color="primary"
          onClick={(e) => openApproveMenu({ target: e.currentTarget, onApprove: onBulkApprove })}
          disabled={disabled}
          style={{ marginRight: 8 }}
        >
          Approve
        </Button>

        <Button
          variant="outlined"
          color="secondary"
          onClick={(e) =>
            openUnapproveMenu({ target: e.currentTarget, onUnapprove: onBulkUnapprove })
          }
          disabled={disabled}
        >
          Unapprove
        </Button>
      </Box>
    );
  }, [hasSelectionOnPage, openApproveMenu, openUnapproveMenu, onBulkApprove, onBulkUnapprove]);

  const renderFooterOptions = useCallback(() => {
    return <TeamsPaginationFooter paginationProps={paginationProps} />;
  }, [paginationProps]);

  const { TableComponent } = useMuiTableBuilderV2({
    ...tableConfig,
    mainList,
    _customRowProps,
    isLoading,
    renderHeaderTools,
    renderFooterOptions,
    tableMaxHeight: '800px',
  });

  return <Box key={`teams-deletion-page-${paginationProps.page}`}>{TableComponent}</Box>;
};

export default DataDeletionReviewTable;
